import React, { useEffect, useState, useRef } from "react";

const SIZE = 5;
const TILE_SIZE = 70;

const IMAGES = [
  "https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?w=800&auto=format&fit=crop&q=80",
  "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=800&auto=format&fit=crop&q=80",
  "https://images.unsplash.com/photo-1501785888041-af3ef285b470?w=800&auto=format&fit=crop&q=80",
  "https://images.unsplash.com/photo-1472214103451-9374bd1c798e?w=800&auto=format&fit=crop&q=80"
];

export default function ImagePuzzle() {
  const [tiles, setTiles] = useState([]);
  const [image, setImage] = useState(IMAGES[0]);
  const startPos = useRef(null);

  useEffect(() => {
    shuffle();
  }, []);

  /* =========================
     🔀 SHUFFLE
  ========================= */
  const shuffle = () => {
    let arr = [...Array(SIZE * SIZE - 1).keys()];
    arr.push(null);

    do {
      arr.sort(() => Math.random() - 0.5);
    } while (!isSolvable(arr));

    setTiles(arr);
  };

  const isSolvable = (arr) => {
    let inv = 0;

    for (let i = 0; i < arr.length; i++) {
      for (let j = i + 1; j < arr.length; j++) {
        if (arr[i] !== null && arr[j] !== null && arr[i] > arr[j]) inv++;
      }
    }

    const emptyRowFromBottom =
      SIZE - Math.floor(arr.indexOf(null) / SIZE);

    return (inv + emptyRowFromBottom) % 2 === 0;
  };

  /* =========================
     🔥 SLIDE LOGIC
  ========================= */
  const slide = (row, col, direction) => {
    const empty = tiles.indexOf(null);

    const emptyRow = Math.floor(empty / SIZE);
    const emptyCol = empty % SIZE;

    let newTiles = [...tiles];

    // horizontal
    if (row === emptyRow) {
      if (direction === "left" && col > emptyCol) {
        for (let i = emptyCol; i < col; i++) {
          newTiles[row * SIZE + i] =
            newTiles[row * SIZE + i + 1];
        }
        newTiles[row * SIZE + col] = null;
      }

      if (direction === "right" && col < emptyCol) {
        for (let i = emptyCol; i > col; i--) {
          newTiles[row * SIZE + i] =
            newTiles[row * SIZE + i - 1];
        }
        newTiles[row * SIZE + col] = null;
      }
    }

    // vertical
    if (col === emptyCol) {
      if (direction === "up" && row > emptyRow) {
        for (let i = emptyRow; i < row; i++) {
          newTiles[i * SIZE + col] =
            newTiles[(i + 1) * SIZE + col];
        }
        newTiles[row * SIZE + col] = null;
      }

      if (direction === "down" && row < emptyRow) {
        for (let i = emptyRow; i > row; i--) {
          newTiles[i * SIZE + col] =
            newTiles[(i - 1) * SIZE + col];
        }
        newTiles[row * SIZE + col] = null;
      }
    }

    setTiles(newTiles);
  };

  /* =========================
     🖱 CLICK MOVE
  ========================= */
  const moveTile = (index) => {
    const empty = tiles.indexOf(null);

    const emptyRow = Math.floor(empty / SIZE);
    const emptyCol = empty % SIZE;

    const row = Math.floor(index / SIZE);
    const col = index % SIZE;

    if (row === emptyRow) {
      if (col > emptyCol) slide(row, col, "left");
      else slide(row, col, "right");
    }

    if (col === emptyCol) {
      if (row > emptyRow) slide(row, col, "up");
      else slide(row, col, "down");
    }
  };

  /* =========================
     📱 TOUCH SUPPORT
  ========================= */
  const handleStart = (e, index) => {
    const touch = e.touches ? e.touches[0] : e;

    startPos.current = {
      x: touch.clientX,
      y: touch.clientY,
      index
    };
  };

  const handleEnd = (e) => {
    if (!startPos.current) return;

    const touch = e.changedTouches ? e.changedTouches[0] : e;

    const dx = touch.clientX - startPos.current.x;
    const dy = touch.clientY - startPos.current.y;

    const index = startPos.current.index;

    const row = Math.floor(index / SIZE);
    const col = index % SIZE;

    if (Math.abs(dx) > Math.abs(dy)) {
      if (dx > 30) slide(row, col, "right");
      else if (dx < -30) slide(row, col, "left");
    } else {
      if (dy > 30) slide(row, col, "down");
      else if (dy < -30) slide(row, col, "up");
    }

    startPos.current = null;
  };

  /* =========================
     🖼 CHANGE IMAGE
  ========================= */
  const changeImage = () => {
    const random =
      IMAGES[Math.floor(Math.random() * IMAGES.length)];
    setImage(random);
    shuffle();
  };

  /* =========================
     🏆 WIN CHECK
  ========================= */
  const isWin = tiles.every((t, i) => {
    if (i === tiles.length - 1) return t === null;
    return t === i;
  });

  return (
    <div className="img-puzzle">
      <h2>🧩 Sliding Puzzle</h2>
      <p style={{ fontSize: "13px", color: "#aaa" }}>
        Tap or swipe tiles to solve the image
      </p>

      {/* 🎮 CONTROLS */}
      <div className="controls">
        <button onClick={changeImage}>🖼 Change Image</button>
        <button onClick={shuffle}>🔀 Shuffle</button>
      </div>

      {/* 🧱 BOARD */}
      <div className="board">
        {tiles.map((tile, i) => {
          if (tile === null) return null;

          const x = (i % SIZE) * TILE_SIZE;
          const y = Math.floor(i / SIZE) * TILE_SIZE;

          return (
            <div
              key={tile}
              className="tile"
              onClick={() => moveTile(i)}
              onMouseDown={(e) => handleStart(e, i)}
              onMouseUp={handleEnd}
              onTouchStart={(e) => handleStart(e, i)}
              onTouchEnd={handleEnd}
              style={{
                transform: `translate(${x}px, ${y}px)`,
                width: TILE_SIZE,
                height: TILE_SIZE,
                backgroundImage: `url(${image})`,
                backgroundSize: `${SIZE * 100}% ${SIZE * 100}%`,
                backgroundPosition: `${
                  (tile % SIZE) * (100 / (SIZE - 1))
                }% ${Math.floor(tile / SIZE) * (100 / (SIZE - 1))}%`
              }}
            />
          );
        })}
      </div>

      {/* 🎉 WIN */}
      {isWin && <div className="win">🎉 Completed!</div>}
    </div>
  );
}